import _ from "lodash";
import React, { useState } from "react";
import classes from "./TableBulkActions.scss";
import { useI18n } from "Hooks";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import {
  onBulkAddTags,
  onDeleteClick,
  onBulkRemoveTags,
  onBulkAddGrades,
  onBulkRemoveGrades,
  getSortedArrayMemoized,
} from "../../Utils/Utils";
import {
  TagOutlined,
  GraduationCapOutlined,
  DeleteOutlined,
  AddCircleOutlined,
  MinusCircleOutlined,
} from "@toddle-edu/ds-icons";
import AddItemsModal from "../Table/Components/TableRow/Components/AddItemsModal/AddItemsModal";
import RemoveItemsModal from "../Table/Components/TableRow/Components/RemoveItemsModal/RemoveItemsModal";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import { createMultiLevelTags } from "NodeEditor/Modules/NodeEditorModule";
import {
  Alert,
  Button,
  ButtonDisclosure,
  Dialog,
  Dropdown,
  DropdownMenu,
} from "@toddle-edu/ds-web";
import { CURRICULUM_TYPE_PYP } from "Constants/stringConstants";

const DROPDOWN_CONFIG = ({ t }) => [
  {
    label: t("common:add_remove_with_label", {
      label: t("common:grade_plural"),
    }),
    icon: <GraduationCapOutlined />,
    options: [
      {
        label: t("common:add_with_label", { label: t("common:grade_plural") }),
        key: "ADD_GRADES",
        icon: <AddCircleOutlined />,
      },
      {
        label: t("common:remove_with_label", {
          label: t("common:grade_plural"),
        }),
        key: "REMOVE_GRADES",
        icon: <MinusCircleOutlined />,
      },
    ],
    id: "GRADES",
  },
  {
    label: t("common:add_remove_with_label", { label: t("common:tags") }),
    icon: <TagOutlined />,
    options: [
      {
        label: t("common:add_with_label", { label: t("common:tags") }),
        key: "ADD_TAGS",
        icon: <AddCircleOutlined />,
      },
      {
        label: t("common:remove_with_label", { label: t("common:tags") }),
        key: "REMOVE_TAGS",
        icon: <MinusCircleOutlined />,
      },
    ],
    id: "TAGS",
  },
];

const TableBulkActions = props => {
  const {
    nodes,
    setNodes,
    selectedStandardsCount,
    selectedNodeIds,
    updateNodesInDraft,
    rootNodeId,
    organizationTags,
    createMultiLevelTags,
    inputForMultiLevelTagsQuery,
    organizationTagsObjectMap,
    userId,
    gradeOptions,
    currentCurriculumProgram,
  } = props;
  const [isDialogueBoxOpen, setIsDialogueBoxOpen] = useState(false);
  // Here items can be GRADES or TAGS
  const [addItemsModalState, setAddItemModalState] = useState("");
  const [removeItemsModalState, setRemoveItemsModalState] = useState("");
  const [itemsList, setItemsList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useI18n(["scopeAndSequence", "common"]);

  const selectedStandardsText = t("common:label_selected", {
    label: selectedStandardsCount,
  });

  const deleteText = t("common:delete");

  const dialogueBoxConfig = {
    modalTitle: t("common:delete_with_label", {
      label: t("scopeAndSequence:standards_plural"),
    }),
    modalBody: (
      <div>
        <div>{t("scopeAndSequence:delete_strand_msg")}</div>
        <div className={classes.dialogBody}>
          <Alert
            type="warning"
            message={
              !_.isEqual(currentCurriculumProgram.type, CURRICULUM_TYPE_PYP)
                ? t("scopeAndSequence:delete_strand_msg_desc_other_curriculum")
                : t("scopeAndSequence:delete_strand_msg_desc")
            }
          />
        </div>
      </div>
    ),
  };

  const handleDeleteClick = () => {
    const { updatedNodes, removedNodes } = onDeleteClick({
      nodes,
      setNodes,
      selectedNodeIds,
      rootNodeId,
      userId,
    });
    updateNodesInDraft({ updatedNodes, removedNodes });
  };

  const toggleDialogueBoxDisplay = () => {
    setIsDialogueBoxOpen(prevState => !prevState);
  };
  //code related to Manage Tags or grades
  const hideAddItemsModal = () => {
    setAddItemModalState("");
    setItemsList([]);
  };
  const hideRemoveItemsModal = () => {
    setRemoveItemsModalState("");
    setItemsList([]);
  };
  const onDropdownItemClick = ({ value, id }) => {
    switch (value) {
      case "ADD_TAGS": {
        setAddItemModalState(id);
        setItemsList([]);
        break;
      }
      case "REMOVE_TAGS": {
        setRemoveItemsModalState(id);
        setItemsList([]);
        break;
      }
      case "ADD_GRADES": {
        setAddItemModalState(id);
        setItemsList([]);
        break;
      }
      case "REMOVE_GRADES": {
        setRemoveItemsModalState(id);
        setItemsList([]);
        break;
      }
      default:
        console.error("other case");
    }
  };

  const handleAddItems = () => {
    let updatedNodes = [];
    if (addItemsModalState === "GRADES") {
      updatedNodes = onBulkAddGrades({
        nodes,
        setNodes,
        selectedNodeIds,
        itemsList,
      });
    } else if (addItemsModalState === "TAGS") {
      updatedNodes = onBulkAddTags({
        nodes,
        setNodes,
        selectedNodeIds,
        itemsList,
      });
    }
    updateNodesInDraft({ updatedNodes });
    hideAddItemsModal();
  };
  //this function creates a new tag
  const onCreateNewTag = async value => {
    const input = [{ label: value }];
    setIsLoading(true);
    const addedTag = await createMultiLevelTags({
      input,
      inputForMultiLevelTagsQuery,
    });
    // The Dnd component needs the value property to render the label of tag
    const newTag = {
      ...addedTag,
      value,
    };
    setIsLoading(false);
    if (newTag) {
      setItemsList(prevTags => [...prevTags, newTag]);
    }
  };

  //code related to bulk remove tags
  const handleRemoveItems = () => {
    let updatedNodes = [];
    if (removeItemsModalState === "GRADES") {
      updatedNodes = onBulkRemoveGrades({
        nodes,
        setNodes,
        selectedNodeIds,
        itemsList,
      });
    } else if (removeItemsModalState === "TAGS") {
      updatedNodes = onBulkRemoveTags({
        nodes,
        setNodes,
        selectedNodeIds,
        itemsList,
      });
    }

    updateNodesInDraft({ updatedNodes });
    hideRemoveItemsModal();
  };
  //cumulative list of tags as input for remove tags modal
  const getCumulativeTagsList = () => {
    const cumulativeTagMap = new Map();
    for (const nodeId of selectedNodeIds) {
      const node = nodes[nodeId];
      const tags = _.get(node, "tags", []);
      for (const tag of tags) {
        if (!_.isEmpty(tag)) {
          cumulativeTagMap.set(tag.id, tag);
        }
      }
    }
    let cumulativeTagsList = [...cumulativeTagMap.values()];
    cumulativeTagsList = _.map(cumulativeTagsList, tag => {
      return { id: tag.id, label: organizationTagsObjectMap[tag.id].label };
    });
    return cumulativeTagsList;
  };

  // localized inner texts and relative functions attached
  const ITEMS_CONFIG = {
    TAGS: {
      ADD: {
        buttonText1: t("common:cancel"),
        buttonText2: t("common:add"),
        modalTitle: t("common:add_with_label", { label: t("common:tags") }),
        searchPlaceholderText: t("scopeAndSequence:enter_a_item", {
          label: t("common:tag"),
        }),

        noItemsText: t("scopeAndSequence:no_item_created", {
          type: t("common:tags"),
        }),
        noItemsSubText: t("scopeAndSequence:go_ahead_tags_desc", {
          msg: t("scopeAndSequence:create_first_tag"),
        }),
        tag: t("common:tag"),
        isCreateItems: true,
        onCreateNewTag: onCreateNewTag,
        userNote: t(
          "scopeAndSequence:tag_note_adding_here_will_add_to_all_standards"
        ),
        createText: t("scopeAndSequence:create_new_tag"),
      },
      REMOVE: {
        buttonText1: t("common:cancel"),
        buttonText2: t("common:remove"),
        modalTitle: t("common:remove_with_label", { label: t("common:tags") }),
        noItemsText: t("scopeAndSequence:no_item_added", {
          type: t("common:tags"),
        }),
        noItemsSubText: t("scopeAndSequence:go_ahead_tags_desc", {
          msg: t("scopeAndSequence:add_first_tag"),
        }),
        tags: t("common:tags"),
        modalDescription: t("scopeAndSequence:remove_items_description", {
          item: t("common:tag"),
          items: t("common:tags"),
        }),
      },
    },
    GRADES: {
      ADD: {
        buttonText1: t("common:cancel"),
        buttonText2: t("common:add"),
        modalTitle: t("common:add_with_label", {
          label: t("common:grade_plural"),
        }),
        searchPlaceholderText: t("scopeAndSequence:enter_a_item", {
          label: t("common:grade"),
        }),

        noItemsSubText: t("scopeAndSequence:go_ahead_tags_desc", {
          msg: t("scopeAndSequence:add_first_grade"),
        }),
        tag: t("common:grade_plural"),
        isCreateItems: false,
        onCreateNewTag: null,
        userNote: t(
          "scopeAndSequence:grade_note_adding_here_will_add_to_all_standards"
        ),
      },
      REMOVE: {
        buttonText1: t("common:cancel"),
        buttonText2: t("common:remove"),
        modalTitle: t("common:remove_with_label", {
          label: t("common:grade_plural"),
        }),
        noItemsText: t("scopeAndSequence:no_item_added", {
          type: t("common:grade_plural"),
        }),
        noItemsSubText: t("scopeAndSequence:go_ahead_tags_desc", {
          msg: t("scopeAndSequence:add_first_grade"),
        }),
        tags: t("common:grade_plural"),
        modalDescription: t("scopeAndSequence:remove_items_description", {
          item: t("common:grade"),
          items: t("common:grade_plural"),
        }),
      },
    },
  };

  const getCumulativeGradesList = () => {
    let grades = [];
    for (const nodeId of selectedNodeIds) {
      const currentNode = nodes[nodeId];
      const currentNodeGrades = currentNode.grades;

      grades = _.unionBy(currentNodeGrades, grades, "id");
    }

    const gradeItems = _.map(grades, ({ id }) => ({
      id,
      label: _.get(gradeOptions[id], "label", ""),
    }));
    return gradeItems;
  };

  const getCumulativeList = () => {
    if (removeItemsModalState === "TAGS") {
      return getCumulativeTagsList();
    } else if (removeItemsModalState === "GRADES") {
      return getCumulativeGradesList();
    }

    return []; // exception case
  };
  const cumulativeList = getCumulativeList();

  const renderDropdowns = _.map(DROPDOWN_CONFIG({ t }), obj => {
    const { label, icon, options, id } = obj;

    return (
      <React.Fragment key={id}>
        <Dropdown
          overlay={
            <DropdownMenu
              options={options}
              onClick={({ key }) => onDropdownItemClick({ value: key, id })}
            />
          }
        >
          <ButtonDisclosure
            variant={"outlined-subtle"}
            label={label}
            icon={icon}
            size={"small"}
          >
            {label}
          </ButtonDisclosure>
        </Dropdown>
      </React.Fragment>
    );
  });

  const getAddItems = () => {
    if (addItemsModalState === "GRADES") {
      return getSortedArrayMemoized({ inputObject: gradeOptions });
    } else if (addItemsModalState === "TAGS") {
      return organizationTags;
    }
    return []; // exception case
  };

  const filterAddItems = getAddItems();
  return (
    <div className={classes.container}>
      <div className={classes.selectedStandards}>{selectedStandardsText}</div>
      <div className={classes.actionsContainer}>
        {renderDropdowns}
        <Button
          variant={"outlined-subtle"}
          size={"small"}
          icon={<DeleteOutlined />}
          onClick={toggleDialogueBoxDisplay}
        >
          {deleteText}
        </Button>
      </div>
      {isDialogueBoxOpen && (
        <Dialog
          onClose={toggleDialogueBoxDisplay}
          title={dialogueBoxConfig.modalTitle}
          message={dialogueBoxConfig.modalBody}
          variant={"delete-dialog"}
          onButtonOneClick={toggleDialogueBoxDisplay}
          onButtonTwoClick={handleDeleteClick}
        />
      )}

      {addItemsModalState && (
        <AddItemsModal
          items={filterAddItems}
          onClickButton1={hideAddItemsModal}
          onClickButton2={handleAddItems}
          buttonText1={ITEMS_CONFIG[addItemsModalState].ADD.buttonText1}
          buttonText2={ITEMS_CONFIG[addItemsModalState].ADD.buttonText2}
          modalTitle={ITEMS_CONFIG[addItemsModalState].ADD.modalTitle}
          searchPlaceholderText={
            ITEMS_CONFIG[addItemsModalState].ADD.searchPlaceholderText
          }
          noItemsSubText={ITEMS_CONFIG[addItemsModalState].ADD.noItemsSubText}
          isCreateItems={ITEMS_CONFIG[addItemsModalState].ADD.isCreateItems}
          onCreateItemClick={
            ITEMS_CONFIG[addItemsModalState].ADD.onCreateNewTag
          }
          selectedItemsList={itemsList}
          setSelectedItemsList={setItemsList}
          userNote={ITEMS_CONFIG[addItemsModalState].ADD.userNote}
          createText={ITEMS_CONFIG[addItemsModalState].ADD.createText}
        />
      )}
      {removeItemsModalState && (
        <RemoveItemsModal
          items={cumulativeList}
          onClickButton1={hideRemoveItemsModal}
          onClickButton2={handleRemoveItems}
          buttonText1={ITEMS_CONFIG[removeItemsModalState].REMOVE.buttonText1}
          buttonText2={ITEMS_CONFIG[removeItemsModalState].REMOVE.buttonText2}
          modalTitle={ITEMS_CONFIG[removeItemsModalState].REMOVE.modalTitle}
          noItemsText={ITEMS_CONFIG[removeItemsModalState].REMOVE.noItemsText}
          noItemsSubText={
            ITEMS_CONFIG[removeItemsModalState].REMOVE.noItemsSubText
          }
          itemName={ITEMS_CONFIG[removeItemsModalState].REMOVE.tags}
          removedItemsList={itemsList}
          setRemovedItemsList={setItemsList}
          modalDescription={
            ITEMS_CONFIG[removeItemsModalState].REMOVE.modalDescription
          }
        />
      )}
      {isLoading && <FullScreenLoader />}
    </div>
  );
};

const mapActionCreators = {
  createMultiLevelTags,
};

export default compose(connect(null, mapActionCreators))(TableBulkActions);
